import { MultipleSelectComponent } from 'src/app/components/multiple-select/multiple-select.component';
import { DataLoader } from 'src/app/services/data-loader/data-loader';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { Factory } from 'src/app/utils/Factory';
import {
    ICreatedInstanceId,
    IGcpProjectId,
    IQueryCacheService,
} from 'src/app/utils/IFactory';
import {
    ICompiledSQLTokens,
    generateTableFullPath,
} from 'src/app/utils/datasource-sql-map/datasource';
import { bq2echartsformat } from 'src/app/utils/echarts/echarts';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { PromiseCancellationToken } from 'src/app/utils/promise/cancellation-token';
import { DataLoaderResult } from '../../../../services/data-loader/data-loader-result';
import { standardRun } from '../../../../services/data-loader/utils/standard-run';
import { BiVizDashboardRetention } from '../sql-maps/biviz-dashboard-retention-sql-map';

export interface RetentionByFirstOpenDateDataLoaderFactoryOptions
    extends ICreatedInstanceId,
        IQueryCacheService {
    /** choose between retention and rolling retention */
    retentionType: 'retention' | 'rolling_retention';

    gcpProjectId: GcpProjectId | (() => GcpProjectId);
}

export class RetentionByFirstOpenDateDataLoaderFactory extends Factory<
    DataLoader,
    RetentionByFirstOpenDateDataLoaderFactoryOptions
> {
    override FACTORY_CLASS: string =
        'RetentionByFirstOpenDateDataLoaderFactory';
    /** This function returns DataLoader instance for analytics vs store reports differences */

    getInstance(options: RetentionByFirstOpenDateDataLoaderFactoryOptions) {
        // let options = verifyRequiredOptions(rawOptions);
        // if (!verifyRequiredOptions(options)) {
        //     throw Error(
        //         '[getRetentionByFirstOpenDateDataLoaderFactory] Provided options are of type SalesDatetimeDataLoaderOptions'
        //     );
        // }
        return new DataLoader(options.createdInstanceId, async function (
            queryContext: IQueryContext,
            cancellationToken: PromiseCancellationToken
        ) {
            const resolvedGcpProjectId: GcpProjectId =
                typeof options.gcpProjectId === 'function'
                    ? options.gcpProjectId()
                    : options.gcpProjectId;

            const process = async () => {
                this.emitter.emit(
                    DataLoaderResult.getLoading(this.latestData, queryContext)
                );

                const datasourceSqlMap = BiVizDashboardRetention.sqlMap;

                // identify columns not supported in datasource
                const dsColumns = DataLoader.separatePresentAndMissingDims(
                    queryContext.datasourceColumns,
                    datasourceSqlMap.columns
                );

                const { pendingDataLoaderResultStatus, pendingStatusMessage } =
                    DataLoader.getMissingColumnsStatus(dsColumns.missing);

                const selectedDayNo =
                    (queryContext.settings[
                        QueryContextSettingID.RETENTION_SELECTED_DAY_NO
                    ] as string[]) ??
                    QueryContextDefaultSettings[
                        QueryContextSettingID.RETENTION_SELECTED_DAY_NO
                    ];

                const selectedDayNoSQL: Partial<ICompiledSQLTokens> = {
                    where: [
                        MultipleSelectComponent.toNumberSQL('day_no', {
                            value: {
                                options: [0, ...selectedDayNo],
                                filterType: 'include',
                            },
                            isFilterApplied: true,
                            isSplitApplied: true,
                        }),
                    ],
                };

                /** ie `3 AS d3, 7 AS d7, 30 AS d30`*/
                const pivotColumns = selectedDayNo
                    .map((d) => `${d} AS d${d}`)
                    .join(',');

                const {
                    dateTrunc,
                    splitSelect,
                    splitGroupName,
                    splitGroupBy,
                    where,
                    join,
                } = BiVizDashboardRetention.compileSQLStatements(
                    dsColumns.present,
                    queryContext.settings,
                    { gcpProjectId: resolvedGcpProjectId },
                    selectedDayNoSQL
                );

                // if retentionType is retention, then assign active_days, if rolling_retention, then assign rolling_days
                const retentionTypeDays =
                    options.retentionType === 'retention'
                        ? 'active_days'
                        : 'rolling_days';

                const query = `
-- DataLoaderId: ${this.instanceId} 
WITH by_fod_and_day_no AS (
    SELECT
      ${splitSelect}
      day_no,
      ${dateTrunc} AS date,
      COUNT(DISTINCT user_id) AS cnt_users,
      FROM \`${generateTableFullPath(
          resolvedGcpProjectId,
          datasourceSqlMap.dataset,
          datasourceSqlMap.tableId
      )}\` ds, UNNEST(${retentionTypeDays}) AS day_no
      ${join}
      ${where}
  
      GROUP BY
        ${splitGroupBy}
        date,
        day_no 
  )
              
  , cohort_size AS (
    SELECT
      ${splitGroupBy}
      f.day_no,
      f.date,
      f.cnt_users,
      ( SELECT cnt_users FROM by_fod_and_day_no f1 WHERE ${
          splitSelect ? `f1.${splitGroupName} = f.${splitGroupName} AND ` : ''
      } f1.date = f.date AND f1.day_no = 0 ) AS cohort_size
    FROM
      by_fod_and_day_no f
  )
      
  , base AS
  (
    SELECT 
      ${splitGroupBy}
      day_no,
      date,
      SUM(cnt_users) AS present,
      SUM(cohort_size.cohort_size) AS cohort,
      ROUND(SAFE_DIVIDE(SUM(cnt_users), SUM(cohort_size.cohort_size)), 4) AS retention
    FROM cohort_size
    GROUP BY ${splitGroupBy}day_no, date
  )
  
  SELECT *
  FROM base
  PIVOT(ANY_VALUE(present) present, ANY_VALUE(retention) retention
    FOR day_no IN (${pivotColumns}))
  ORDER BY ${splitGroupBy}date
`;

                const queryResponse = await DataLoader.runQuery(
                    query,
                    options.queryCacheService
                );
                cancellationToken.throwIfCancellationRequested();
                var data = [...queryResponse.result];
                bq2echartsformat(data);

                this.emitter.emit({
                    data,
                    queryContext,
                    status: pendingDataLoaderResultStatus,
                    statusMessage: pendingStatusMessage,
                    isLoading: false,
                    sqlAndJsProcessing: query,
                });
            };

            await standardRun(this, queryContext, process);
        });
    }
}
